import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Star, Clock } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do coaches and consultants create invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Coaches and consultants can create professional invoices using Invilic. Choose the "Coach / Consultant" invoice type, add your name, your client\'s details, describe the sessions or services provided (e.g. "3x 1-hour coaching sessions"), set your rate, and download a PDF, completely free.',
      },
    },
    {
      '@type': 'Question',
      name: 'What should a coaching invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A coaching invoice should include: your name and business name; client name; invoice number and date; description of services (session type, number of sessions, date range); per-session rate or package price; total amount due; payment due date and payment instructions.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do coaches need to charge GST in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If a coach\'s annual revenue from coaching services exceeds ₹20 lakh (or ₹10 lakh in special category states), they must register for GST and charge 18% GST (SAC code 999291 for coaching services). Below the threshold, a standard invoice without GST is sufficient.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a retainer invoice for consulting?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Add a single line item for "Monthly retainer - [Month]" with your retainer fee. You can also add additional line items for out-of-scope work billed separately. Download the PDF and send to your client.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I bill by the session or by the package?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both. Use individual line items to bill by session (e.g. "1:1 coaching session, 60 min x 4 sessions at ₹5,000 each"), or a single line item for a package price (e.g. "3-month coaching package, ₹45,000"). Invilic calculates totals automatically.',
      },
    },
  ],
};

const coachTypes = [
  { title: 'Life & career coaches', desc: 'Bill for 1:1 sessions, group workshops, or month-long programs.' },
  { title: 'Business consultants', desc: 'Invoice for advisory hours, project deliverables, or monthly retainers.' },
  { title: 'Fitness & wellness coaches', desc: 'Create invoices for personal training sessions or wellness packages.' },
  { title: 'Executive coaches', desc: 'Bill corporate clients for leadership coaching programs or team workshops.' },
  { title: 'Online course creators', desc: 'Invoice for course access, mentorship programs, or group cohorts.' },
  { title: 'Freelance trainers', desc: 'Bill organizations for corporate training sessions or seminars.' },
];

export default function CoachInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Coach & Consultant Invoice Generator: Free Professional Invoice | Invilic"
        description="Create professional coach and consultant invoices online free. Bill per session, package, or retainer. Add GST if registered. Download PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/coach-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Coaches & Consultants
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Coach & Consultant Invoice Generator
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create professional invoices for coaching sessions, consulting engagements, and retainers. Bill per session, per package, or per project. Download a polished PDF for Free, no account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/coach"
              className="inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-7 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200"
            >
              Create Coach Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold px-7 py-4 rounded-xl transition-all duration-200"
            >
              Consultant Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Who uses it */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Coach and Consultant
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {coachTypes.map(({ title, desc }) => (
              <div key={title} className="flex items-start gap-3 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Billing methods */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Bill Clients the Way You Work
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Clock,
                title: 'Per session',
                example: '"1:1 coaching, 60 min x 4 = ₹20,000"',
                desc: 'Add sessions as line items with quantity and per-session rate.',
              },
              {
                icon: Star,
                title: 'Package pricing',
                example: '"3-month transformation program: ₹60,000"',
                desc: 'Single line item with a flat package fee.',
              },
              {
                icon: Users,
                title: 'Monthly retainer',
                example: '"Advisory retainer, May 2025: $2,000"',
                desc: 'Recurring invoice with one line item for the retainer amount.',
              },
            ].map(({ icon: Icon, title, example, desc }) => (
              <div key={title} className="p-6 rounded-2xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-xs text-violet-700 dark:text-violet-300 italic mb-3">{example}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* GST note for India coaches */}
        <section className="mb-16 p-6 rounded-2xl border border-emerald-100 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-2">GST on Coaching Services in India</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Coaching and consulting services attract 18% GST (SAC code 999291) if you are GST-registered. If your annual revenue exceeds ₹20 lakh, you must register and charge GST.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/create/gst"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
            >
              Create GST invoice for coaching <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/create/coach"
              className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:underline"
            >
              Standard invoice (no GST) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* UK VAT note for UK coaches */}
        <section className="mb-16 p-6 rounded-2xl border border-violet-100 dark:border-violet-800/40 bg-violet-50 dark:bg-violet-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-2">VAT on Coaching Services in the UK</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Coaching services in the UK are standard-rated at <strong className="font-semibold text-gray-900 dark:text-white">20% VAT</strong>. You must register with HMRC and charge VAT once your taxable turnover exceeds <strong className="font-semibold text-gray-900 dark:text-white">£90,000</strong> in any rolling 12-month period. Below this threshold, your invoice total is the full amount due — no VAT line required.
          </p>
          <div className="space-y-2 mb-4">
            {[
              'Life coaching, business coaching, executive coaching, and career coaching — all standard-rated at 20% VAT if VAT registered',
              'Health coaching by regulated health professionals (e.g. nurses, physiotherapists) may qualify for VAT exemption — check HMRC Notice 701/57',
              'Voluntary VAT registration available below £90,000 — useful if your clients are VAT-registered businesses who can reclaim',
            ].map(item => (
              <div key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <Link
            to="/blog/how-to-invoice-as-a-coach-uk"
            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-400 hover:underline"
          >
            Full UK coaching invoice guide <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* What's on the invoice */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's on Your Coach Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name / coaching business name & logo',
              'Client name and contact details',
              'Invoice number and date',
              'Session or package description',
              'Rate per session or package total',
              'Tax (GST 18% if applicable)',
              'Total amount due',
              'Payment due date',
              'Bank / UPI / payment link details',
              'Terms, notes, and signature',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-violet-100 dark:border-violet-800/30 bg-violet-50 dark:bg-violet-900/10">
                <CheckCircle className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Coach & Consultant Invoice: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Coach Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in under 2 minutes. No account needed.</p>
          <Link
            to="/create/coach"
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-600/20 transition-all duration-200 text-lg"
          >
            Create Coach Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

